import React, { useState, useEffect } from 'react';
import useAPI from '../hooks/useAPI';
import {
  getTechnicians, createTechnician, updateTechnician,
  deleteTechnician, importTechnicianCSV, getProjectsForDropdown,
} from '../services/api';

/**
 * Manager dashboard page for technician management.
 * Add/edit/delete technicians + CSV import.
 */
export default function TechnicianManagement() {
  const { data, loading, error, reload } = useAPI(() => getTechnicians(), []);
  const [projects, setProjects] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ fullName: '', phoneNumber: '', projects: [], role: 'technician', countryCode: 'IL', email: '' });
  const [filterProject, setFilterProject] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [csvStatus, setCsvStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    getProjectsForDropdown().then(d => setProjects(d.projects || [])).catch(() => {});
  }, []);

  const techs = (data?.technicians || []).filter(t => {
    if (filterProject && !(t.projects || []).includes(filterProject)) return false;
    if (filterCountry && t.countryCode !== filterCountry) return false;
    return true;
  });

  const handleSave = async () => {
    setActionLoading(true);
    try {
      if (editId) {
        await updateTechnician(editId, form);
      } else {
        await createTechnician(form);
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ fullName: '', phoneNumber: '', projects: [], role: 'technician', countryCode: 'IL', email: '' });
      reload();
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete technician "${name}"?`)) return;
    try {
      await deleteTechnician(id);
      reload();
    } catch (e) { alert(e.message); }
  };

  const handleEdit = (tech) => {
    setEditId(tech.id);
    setForm({
      fullName: tech.fullName,
      phoneNumber: tech.phoneNumber,
      projects: tech.projects || [],
      role: tech.role,
      countryCode: tech.countryCode,
      email: tech.email || '',
    });
    setShowAdd(true);
  };

  const handleCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvStatus({ loading: true });
    try {
      const result = await importTechnicianCSV(file);
      setCsvStatus(result);
      reload();
    } catch (err) {
      setCsvStatus({ error: err.message });
    }
  };

  const downloadTemplate = () => {
    const csv = 'name,phone,project_id,role,country_code,email\nJohn Doe,+972-54-1234567,,technician,IL,john@example.com\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'technician_template.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Technician Management</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setShowAdd(true); setEditId(null); setForm({ fullName: '', phoneNumber: '', projects: [], role: 'technician', countryCode: 'IL', email: '' }); }} style={styles.addBtn}>
            + Add Technician
          </button>
          <label style={styles.csvBtn}>
            Import CSV
            <input type="file" accept=".csv" onChange={handleCSV} style={{ display: 'none' }} />
          </label>
          <button onClick={downloadTemplate} style={styles.templateBtn}>
            Download Template
          </button>
        </div>
      </div>

      {/* CSV status */}
      {csvStatus && (
        <div style={{
          ...styles.alert,
          background: csvStatus.error ? '#ffeaea' : '#e8f8f5',
          color: csvStatus.error ? '#c0392b' : '#27ae60',
        }}>
          {csvStatus.loading ? 'Importing...' :
           csvStatus.error ? `Error: ${csvStatus.error}` :
           `Imported ${csvStatus.created_count} technicians. ${csvStatus.error_count || 0} errors.`}
          {csvStatus.errors?.length > 0 && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {csvStatus.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
            </div>
          )}
          <span onClick={() => setCsvStatus(null)} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 8 }}>x</span>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filterRow}>
        <select
          value={filterCountry}
          onChange={e => setFilterCountry(e.target.value)}
          style={styles.select}
        >
          <option value="">All Countries</option>
          <option value="IL">Israel</option>
          <option value="IN">India</option>
        </select>
        <select
          value={filterProject}
          onChange={e => setFilterProject(e.target.value)}
          style={styles.select}
        >
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <span style={{ fontSize: 13, color: '#888' }}>{techs.length} technicians</span>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div style={styles.formCard}>
          <h3 style={{ margin: '0 0 12px 0' }}>{editId ? 'Edit Technician' : 'Add Technician'}</h3>
          <div style={styles.formGrid}>
            <input
              placeholder="Full Name *"
              value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })}
              style={styles.input}
            />
            <input
              placeholder="Phone Number *"
              value={form.phoneNumber}
              onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
              style={styles.input}
            />
            <input
              placeholder="Email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              style={styles.input}
            />
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              style={styles.input}
            >
              <option value="technician">Technician</option>
              <option value="senior">Senior</option>
              <option value="lead">Lead</option>
            </select>
            <select
              value={form.countryCode}
              onChange={e => setForm({ ...form, countryCode: e.target.value })}
              style={styles.input}
            >
              <option value="IL">Israel</option>
              <option value="IN">India</option>
              <option value="AE">UAE</option>
            </select>
            <select
              value={form.projects[0] || ''}
              onChange={e => setForm({ ...form, projects: e.target.value ? [e.target.value] : [] })}
              style={styles.input}
            >
              <option value="">Select Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.country})</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={handleSave} disabled={!form.fullName || !form.phoneNumber || actionLoading} style={styles.saveBtn}>
              {actionLoading ? 'Saving...' : (editId ? 'Update' : 'Add')}
            </button>
            <button onClick={() => { setShowAdd(false); setEditId(null); }} style={styles.cancelBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Loading...</div> :
       error ? <div style={{ padding: 32, color: '#c0392b' }}>{error}</div> : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Country</th>
                <th style={styles.th}>Projects</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {techs.map(tech => (
                <tr key={tech.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={styles.avatar}>{tech.fullName?.[0]?.toUpperCase()}</span>
                      {tech.fullName}
                    </div>
                  </td>
                  <td style={styles.td}>{tech.phoneNumber}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      background: tech.role === 'lead' ? '#6c5ce720' :
                                  tech.role === 'senior' ? '#00b89420' : '#f0f0f0',
                      color: tech.role === 'lead' ? '#6c5ce7' :
                             tech.role === 'senior' ? '#00b894' : '#666',
                    }}>
                      {tech.role}
                    </span>
                  </td>
                  <td style={styles.td}>{tech.countryCode}</td>
                  <td style={styles.td}>
                    <div style={{ fontSize: 12 }}>
                      {(tech.projectNames || []).join(', ') || '-'}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => handleEdit(tech)} style={styles.actionBtn}>Edit</button>
                    <button onClick={() => handleDelete(tech.id, tech.fullName)} style={{ ...styles.actionBtn, color: '#c0392b' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: '#2d3436' },
  addBtn: {
    padding: '8px 16px', background: '#6c5ce7', color: '#fff', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
  csvBtn: {
    padding: '8px 16px', background: '#0984e3', color: '#fff', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
  templateBtn: {
    padding: '8px 16px', background: '#fff', color: '#666', border: '1px solid #ddd',
    borderRadius: 8, cursor: 'pointer', fontSize: 13,
  },
  alert: {
    padding: '10px 16px', borderRadius: 8, marginBottom: 16, display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', fontSize: 13,
  },
  filterRow: {
    display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center',
  },
  select: {
    padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd',
    fontSize: 13, background: '#fff',
  },
  formCard: {
    background: '#fff', padding: 20, borderRadius: 12, marginBottom: 20,
    border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  formGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
  },
  input: {
    padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6,
    fontSize: 13, width: '100%', boxSizing: 'border-box',
  },
  saveBtn: {
    padding: '8px 20px', background: '#6c5ce7', color: '#fff', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
  },
  cancelBtn: {
    padding: '8px 20px', background: '#f5f5f5', color: '#666', border: '1px solid #ddd',
    borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee',
    color: '#888', fontWeight: 600, fontSize: 12, textTransform: 'uppercase',
  },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 12px', verticalAlign: 'middle' },
  avatar: {
    width: 28, height: 28, borderRadius: '50%', background: '#6c5ce7',
    color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
  },
  badge: {
    padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    textTransform: 'capitalize',
  },
  actionBtn: {
    background: 'none', border: 'none', color: '#0984e3', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, padding: '2px 6px',
  },
};
